import apiClient from './client';
import type {
  ResourceWithDetails,
  ResourceCategory,
  ResourceCounts,
  ResourceFilterOptions,
  CreateFileResourceRequest,
  CreateLinkResourceRequest,
  UpdateResourceRequest,
  PresignedUploadResponse,
  PaginatedResponse,
  PaginationParams,
} from '../../types';

/**
 * Build query string from resource filters
 */
function buildResourceQueryString(
  filters?: ResourceFilterOptions & PaginationParams,
): string {
  if (!filters) return '';

  const params = new URLSearchParams();

  if (filters.page) {
    params.set('page', String(filters.page));
  }
  if (filters.limit) {
    params.set('limit', String(filters.limit));
  }
  if (filters.sortBy) {
    params.set('sortBy', filters.sortBy);
  }
  if (filters.sortOrder) {
    params.set('sortOrder', filters.sortOrder);
  }
  if (filters.session_ids && filters.session_ids.length > 0) {
    params.set('session_ids', filters.session_ids.join(','));
  }
  if (filters.category_id) {
    params.set('category_id', filters.category_id);
  }
  if (filters.search) {
    params.set('search', filters.search);
  }
  if (filters.access_level) {
    params.set('access_level', filters.access_level);
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * API for managing resources
 */
export const resourcesApi = {
  /**
   * Get resources for a cohort (includes visible evergreen resources)
   */
  getResourcesForCohort: async (
    cohortId: string,
    filters?: ResourceFilterOptions & PaginationParams,
  ): Promise<PaginatedResponse<ResourceWithDetails>> => {
    const queryString = buildResourceQueryString(filters);
    return apiClient.getPaginated<ResourceWithDetails>(
      `/api/cohorts/${cohortId}/resources${queryString}`,
    );
  },

  /**
   * MED-1393: the coach route. The staff route above refuses a coach. This
   * one returns coach-visible resources of a cohort the coach works in.
   */
  getCoachResourcesForCohort: async (
    cohortId: string,
    filters?: ResourceFilterOptions,
  ): Promise<PaginatedResponse<ResourceWithDetails>> => {
    const queryString = buildResourceQueryString(filters);
    return apiClient.getPaginated<ResourceWithDetails>(
      `/api/coach/cohorts/${cohortId}/resources${queryString}`,
    );
  },

  /**
   * Get resource counts for a cohort
   */
  getCountsForCohort: async (cohortId: string): Promise<ResourceCounts> => {
    return apiClient.get<ResourceCounts>(`/api/cohorts/${cohortId}/resources/counts`);
  },

  /**
   * Get all evergreen resources (admin)
   */
  getEvergreenResources: async (
    filters?: ResourceFilterOptions & PaginationParams,
  ): Promise<PaginatedResponse<ResourceWithDetails>> => {
    const queryString = buildResourceQueryString(filters);
    return apiClient.getPaginated<ResourceWithDetails>(
      `/api/resources/evergreen${queryString}`,
    );
  },

  /**
   * Get a single resource by ID
   */
  getById: async (id: string): Promise<ResourceWithDetails> => {
    return apiClient.get<ResourceWithDetails>(`/api/resources/${id}`);
  },

  /**
   * Get all active resource categories
   */
  getCategories: async (): Promise<ResourceCategory[]> => {
    return apiClient.get<ResourceCategory[]>('/api/resources/categories');
  },

  /**
   * Get presigned URL for uploading a file
   */
  getUploadUrl: async (
    filename: string,
    contentType: string,
    cohortId?: string,
  ): Promise<PresignedUploadResponse> => {
    return apiClient.post<PresignedUploadResponse>('/api/resources/upload-url', {
      filename,
      content_type: contentType,
      cohort_id: cohortId,
    });
  },

  /**
   * Create a file resource (after uploading to S3)
   */
  createFileResource: async (
    data: CreateFileResourceRequest,
  ): Promise<ResourceWithDetails> => {
    return apiClient.post<ResourceWithDetails>('/api/resources/file', data);
  },

  /**
   * Create a link resource
   */
  createLinkResource: async (
    data: CreateLinkResourceRequest,
  ): Promise<ResourceWithDetails> => {
    return apiClient.post<ResourceWithDetails>('/api/resources/link', data);
  },

  /**
   * Update a resource
   */
  update: async (
    id: string,
    data: UpdateResourceRequest,
  ): Promise<ResourceWithDetails> => {
    return apiClient.put<ResourceWithDetails>(`/api/resources/${id}`, data);
  },

  /**
   * Delete a resource (soft delete)
   */
  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/api/resources/${id}`);
  },

  /**
   * Hide an evergreen resource from a cohort
   */
  hideEvergreenFromCohort: async (
    cohortId: string,
    resourceId: string,
  ): Promise<{ hidden: boolean }> => {
    return apiClient.post<{ hidden: boolean }>(
      `/api/cohorts/${cohortId}/resources/${resourceId}/hide`,
    );
  },

  /**
   * Unhide an evergreen resource from a cohort
   */
  unhideEvergreenFromCohort: async (
    cohortId: string,
    resourceId: string,
  ): Promise<{ hidden: boolean }> => {
    return apiClient.delete<{ hidden: boolean }>(
      `/api/cohorts/${cohortId}/resources/${resourceId}/hide`,
    );
  },
};

export default resourcesApi;
